import { CreditAmount } from '@/functions/credit-functions';
import { PromotionType } from '@/states/promotion-state';


const getDocument = (document: string, documentType: string | undefined): DocumentType | null => {

    if (documentType === 'DraftTeamtailorJob') {
        return JSON.parse(document) as TeamtailorDocument;
    }
    else if (documentType === 'DraftJobylonJob') {
        return JSON.parse(document) as JobylonDocument;
    }
    else if (documentType === 'DraftAcademicWorkJob') {
        return JSON.parse(document) as AcademicWorkDocument;
    }
    else if (documentType === 'DraftPontyJob') {
        return JSON.parse(document) as PontyJobDocument;
    }
    else if (documentType === 'DraftIntensoJob') {
        return JSON.parse(document) as IntensoJobDocument;
    }
    else if (documentType === 'DraftJobTipJob') {
        return JSON.parse(document) as JobTipJobDocument;
    }
    else if (documentType === 'DraftRecmanJob') {
      return JSON.parse(document) as RecmanJobDocument;
    }
    else if (documentType === 'DraftHaileyJob') {
      return JSON.parse(document) as HaileyDocument;
    }
    else {
        return null;
    }
}

export const fromResponse = (response: UserTaskResponseItem, existing: UserTask | undefined = undefined): UserTask => {
  return {
    ...(existing ? existing : {}),
    ...response,
    createdAt: new Date(response.createdAt),
    completedAt: response.completedAt ? new Date(response.completedAt) : undefined,
    jobPostingLastPublishedAt: response.jobPostingLastPublishedAt ? new Date(response.jobPostingLastPublishedAt) : undefined,
    jobPostingPublishedDate: response.jobPostingPublishedDate ? new Date(response.jobPostingPublishedDate) : undefined,
    jobAdCreatedAt: response.jobAdCreatedAt ? new Date(response.jobAdCreatedAt) : undefined,
    document: getDocument(response.document, response.documentType),
  } as UserTask;
};

export type JobylonLocationObj = {
  location: JobylonLocation;
};

export type JobylonLocation = {
  url?: string;
  city?: string;
  area_1?: string;
  area_2?: string;
  country?: string;
  place_id?: string;
  city_short?: string;
  area_1_short?: string;
  area_2_short: string;
  country_short?: string;
  text?: string;
  postal_code?: string;
  municipality?: string;
  postal_code_short?: string;
};


export type Requirement = 'Company.Credits.Required' |

  'Company.Credits.Volume.Insufficient' |
  'Company.Credits.Basic.Insufficient' |
  'Company.Credits.Boost.Insufficient' |
  'Company.Credits.BoostPlus.Insufficient' |
  'Company.Credits.BoostPro.Insufficient' |

  'Company.Integration.JobylonService.Required' |
  'Company.Integration.TeamtailorXmlService.Required' |
  'Company.Integration.TeamtailorWebhookService.Required' |
  'Company.Integration.HaileyApiService.Required' |

  // This is when an incomming job posting for a company that's not integrated with teamtailor xml
  'JobPosting.Company.Integration.TeamtailorXmlService.Required' |

  'Company.SalesPerson.Required' |

  `Company.DefaultPromotionType.TeamtailorWebhookService.Required` |
  `Company.DefaultPromotionType.TeamtailorXmlService.Required` |
  `Company.DefaultPromotionType.JobylonService.Required` |
  `Company.DefaultPromotionType.HaileyApiService.Required` |

  'JobPosting.Promotion.Failed' |
  'JobPosting.Invalid' |

  'JobPosting.Location.MultipleLocations' |
  'JobPosting.Location.City.Invalid' |
  'JobPosting.Location.Country.Invalid';

export type Location = {
  city: string | null;
  country: string | null;
  isRemote: boolean;
};

export type JobylonDocument = {
  Job: {
    locations: JobylonLocationObj[];
    urls: {
      ad: string;
      apply: string;
    };
  };
  Paid: boolean;
  CompanyId: number;
  ServiceId: number;
};

export type TeamtailorLocation = {
  Country?: string;
  City?: string;
  PostalCode?: string;
  Address?: string;
  CountryCode?: string;
  Zip?: string;
  Region?: string;
};

export type TeamtailorDocument = {
  Job: {
    Locations: TeamtailorLocation[];
    Headquarters: TeamtailorLocation;
    Url: string;
  };
  IntegrationId: string;
  PublicId: string;
  CompanyId: number;
};

export type AcademicWorkDocument = {
  Job: {
    locations: { city?: string; country?: string }[];
    city?: string;
    country?: string;
    advertUrl?: string;
    applicationUrl?: string;
  };
  CompanyId: number;
  ServiceId: number;
};

export type IntensoJobDocument = {
  Job: {
    Title: string;
    CustomTemplateName: string;
    Location: string;
  };
  CompanyId: number;
  ServiceId: number;
};

export type PontyJobDocument = {
  Job: {
    AssignmentId: number;
    ApplyUrl: string;
    Confidential: boolean;
    Location: '' | string | null;
    Region: '' | string | null;
    WithdrawalDate: '' | string | null;
  };
  CompanyId: number;
  ServiceId: number;
};

export type JobTipJobDocument = {
  Job: {
    externalAdId: string;
    subject: string;
    place: string;
    applyUrl: string;
  };
  CompanyId: number;
  ServiceId: number;
};

export type RecmanLocation = {
  Address?: string;
  CityName?: string;
  CountryName?: string;
  Latitude?: string;
  Longitude?: string;
  Name?: string;
  PlaceId?: string;
  RegionName?: string;
};

export type RecmanJobDocument = {
  Job: {
    Name?: string;
    Title?: string;
    Body?: string;
    Type?: 'Permanent' | 'Temporary' | 'Contract' | 'Project' | 'Trainee' | 'Seasonal' | 'SelfEmployed';
    EmploymentType?: 'FullTime' | 'PartTime' | 'Shiftwork';
    Locations?: RecmanLocation[];
    WorkLocation?: 'FromOffice' | 'Remote' | 'Hybrid';
  };
  CompanyId: number;
  ServiceId: number;
};

export type HaileyLocation = {
  Id?: string;
  Name?: string;
  Address?: string;
  City?: string;
  PostalCode?: string;
  Country?: string;
  Phone?: string;
};

export type HaileyDocument = {
  Job: {
    JobAdId: string;
    Title?: string;
    LinkToJobAdOnCareerPage?: string;
    ShowLocation: boolean;
    Location?: HaileyLocation;
  };
  CompanyId: number;
  ServiceId: number;
};

export type DocumentType =
  | TeamtailorDocument
  | JobylonDocument
  | AcademicWorkDocument
  | PontyJobDocument
  | IntensoJobDocument
  | JobTipJobDocument
  | RecmanJobDocument
  | HaileyDocument;

export type DocumentTypeName =
  | 'DraftJobylonJob'
  | 'DraftTeamtailorJob'
  | 'DraftAcademicWorkJob'
  | 'DraftPontyJob'
  | 'DraftIntensoJob'
  | 'DraftJobTipJob'
  | 'DraftRecmanJob'
  | 'DraftHaileyJob';

export type DocumentTypeMap = {
  'DraftJobylonJob': JobylonDocument;
  'DraftTeamtailorJob': TeamtailorDocument;
  'DraftAcademicWorkJob': AcademicWorkDocument;
  'DraftPontyJob': PontyJobDocument;
  'DraftJobTipJob': JobTipJobDocument;
  'DraftIntensoJob': IntensoJobDocument;
  'DraftRecmanJob': RecmanJobDocument;
  'DraftHaileyJob': HaileyDocument;
};

export type WithDocument =
  { document?: undefined; documentType?: undefined } | 
  {
    [K in DocumentTypeName]: {
      document: DocumentTypeMap[K] | null;
      documentType: K;
    }
  }[DocumentTypeName];

export type ServiceName =
  | 'Jobylon'
  | 'TeamtailorWebhook'
  | 'TeamtailorXml'
  | 'Ponty'
  | 'Intenso'
  | 'JobTip'
  | 'HaileyApiService';

export type ServiceDefaultCredit = {
  name: ServiceName;
  defaultCreditType?: PromotionType;
};

export type RequirementResponseDto = {
  value: string;
  isSuccess: boolean;
  isCompleted: boolean;
  isIgnored: boolean;
};

export type UserTask = WithDocument & {
  id: string;
  jobPostingId?: number;
  jobPostingPromotionType: PromotionType | null;
  jobPostingLastPublishedAt?: Date;
  jobPostingPublishedDate?: Date;
  jobAdCreatedAt?: Date;
  companyId: number;
  companyName: string;
  title: string;
  userId: string[];
  createdAt: Date;
  completedAt?: Date;
  requirements: Requirement[];
  completedRequirements: Requirement[];
  ignoredRequirements: Requirement[];
  invalidReasons: string[];
  canUseCredits: boolean;
  creditAmounts: CreditAmount[];
  defaultCredits: ServiceDefaultCredit[];
};

export type StringifyDates<T> = {
  [K in keyof T]: T[K] extends (Date | undefined) ? string : T[K];
};

export type UserTaskResponseItem = Omit<StringifyDates<UserTask>, 'document' | 'requirements' | 'completedRequirements' | 'ignoredRequirements'> & {
  document: string;
};
